/**
 * useBreadcrumb — Retorna os itens de navegação para o Breadcrumb global.
 * Padrão: Home > Nome da Página (ou Home > Configurações > Sub-página).
 */
import { useMemo } from 'react';
import { Page } from '../types';
import { useAppContext } from '../contexts/AppContext';

export interface BreadcrumbItem {
    label: string;
    page?: Page;
}

const HOME_ITEM: BreadcrumbItem = { label: 'Home', page: 'dashboard' };

const SETTINGS_PAGES: Partial<Record<Page, string>> = {
    settings: 'Geral',
    team: 'Membros',
    connections: 'Canais',
    tracking: 'Integrações',
    monitoring: 'Utilização',
    profile: 'Meus Dados',
    billing: 'Faturamento',
    products: 'Produtos',
    'settings-tags': 'Etiquetas',
    'settings-quick-replies': 'Respostas Rápidas',
    'settings-pix': 'Chave PIX',
    'settings-origins': 'Origens',
    'settings-loss-reasons': 'Motivos de Perda',
    'settings-auto-movement': 'Automação',
    'follow-up': 'Follow-up',
    'settings-webhooks': 'Webhook',
};

const PAGE_LABELS: Partial<Record<Page, string>> = {
    dashboard: 'Dashboard',
    chat: 'Conversas',
    kanban: 'Negociações',
    contacts: 'Contatos',
    tasks: 'Tarefas',
    'create-funnel': 'Novo Funil',
    'edit-funnel': 'Funil de Venda',
    subscription: 'Planos',
};

export const useBreadcrumb = (): BreadcrumbItem[] => {
    const { currentPage } = useAppContext();

    return useMemo(() => {
        // Dashboard: só "Home" pois já é a raiz
        if (currentPage === 'dashboard') {
            return [HOME_ITEM];
        }

        // Settings sub-pages: Home > Configurações > Sub-página
        if (SETTINGS_PAGES[currentPage] !== undefined) {
            return [
                HOME_ITEM,
                { label: 'Configurações', page: 'settings' },
                { label: SETTINGS_PAGES[currentPage]! },
            ];
        }

        // Demais páginas: Home > Nome
        if (PAGE_LABELS[currentPage]) {
            return [
                HOME_ITEM,
                { label: PAGE_LABELS[currentPage]! },
            ];
        }

        return [HOME_ITEM];
    }, [currentPage]);
};
